/**
 * Product-card hover dissolve, restored as a single shared WebGL context.
 *
 * The previous implementation created one context per card, which exhausted the
 * browser's WebGL context cap on collection pages and evicted the PDP gallery's
 * context. This owns exactly one context for the whole page: whichever cards are
 * hovering are rendered in turn and blitted into their own cheap 2D canvas, so
 * the context count never grows past one regardless of how many cards exist or
 * how many arrive later via AJAX.
 */
class CardShader {
  constructor () {
    this.entries = new Map()
    this.active = new Set()
    this.order = []
    this.gl = null
    this.ready = false
    this.frame = null
    this.blitBroken = false
    this.blitVerified = false
    this.debug = location.search.indexOf('shaderdebug') !== -1

    this.tick = this.tick.bind(this)
    this.onOver = this.onOver.bind(this)
    this.onOut = this.onOut.bind(this)
    this.onVisibility = this.onVisibility.bind(this)
  }

  init () {
    if (!this.supported) return

    document.addEventListener('mouseover', this.onOver, { passive: true })
    document.addEventListener('mouseout', this.onOut, { passive: true })
    document.addEventListener('visibilitychange', this.onVisibility)

    if (document.querySelector('.prd-Card_Image-hover img')) {
      this.whenIdle(() => this.ensureGL())
    }
  }

  /** Desktop pointers only — the effect is meaningless without a real hover. */
  get supported () {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  onOver (e) {
    const card = e.target.closest && e.target.closest('.prd-Card')
    if (!card || card.contains(e.relatedTarget)) return
    const media = card.querySelector('.prd-Card_Media')
    if (media) this.enter(media)
  }

  onOut (e) {
    const card = e.target.closest && e.target.closest('.prd-Card')
    if (!card || card.contains(e.relatedTarget)) return
    const media = card.querySelector('.prd-Card_Media')
    if (media) this.leave(media)
  }

  onVisibility () {
    if (document.visibilityState === 'visible' && this.active.size) this.schedule()
  }

  enter (media) {
    if (this.blitBroken || !this.hasHover(media)) return
    const entry = this.entryFor(media)
    if (entry.failed) return

    this.touch(media)
    entry.target = 1

    if (entry.texFrom) {
      if (!entry.enhanced) this.enhance(entry)
      this.activate(entry)
    } else {
      media.classList.add('is-Loading')
      if (!entry.loading) this.loadTextures(entry)
    }
  }

  leave (media) {
    const entry = this.entries.get(media)
    if (!entry || entry.failed) return

    entry.target = 0
    media.classList.remove('is-Loading')
    if (entry.enhanced) this.activate(entry)
  }

  hasHover (media) {
    return !!media.querySelector('.prd-Card_Image-hover img')
  }

  entryFor (media) {
    let entry = this.entries.get(media)
    if (entry) return entry

    entry = {
      media,
      canvas: null,
      ctx: null,
      texFrom: null,
      texTo: null,
      fromRatio: 1,
      toRatio: 1,
      progress: 0,
      target: 0,
      seed: [Math.random(), Math.random()],
      enhanced: false,
      loading: false,
      failed: false,
    }
    this.entries.set(media, entry)
    this.order.push(media)
    this.evict()
    return entry
  }

  loadTextures (entry) {
    if (!this.ensureGL()) return
    entry.loading = true

    const from = new Image()
    const to = new Image()
    from.crossOrigin = 'anonymous'
    to.crossOrigin = 'anonymous'

    let pending = 2
    const done = () => {
      if (--pending) return
      entry.loading = false
      entry.media.classList.remove('is-Loading')
      if (entry.failed) return

      this.uploadTextures(entry, from, to)
      if (entry.target === 1 && !entry.enhanced) {
        this.enhance(entry)
        this.activate(entry)
      }
    }
    from.onload = done
    to.onload = done
    from.onerror = to.onerror = () => {
      entry.loading = false
      entry.failed = true
      entry.media.classList.remove('is-Loading')
    }

    from.src = this.bestSource(entry.media.querySelector('.prd-Card_Image:not(.prd-Card_Image-hover) img'))
    to.src = this.bestSource(entry.media.querySelector('.prd-Card_Image-hover img'))
  }

  uploadTextures (entry, from, to) {
    const gl = this.gl
    if (!gl || gl.isContextLost()) return
    try {
      entry.fromRatio = this.imageRatio(from)
      entry.toRatio = this.imageRatio(to)
      entry.texFrom = this.makeTexture(from)
      entry.texTo = this.makeTexture(to)
    } catch (e) {
      entry.failed = true
      entry.texFrom = null
      entry.texTo = null
      this.log('texture upload failed: ' + e)
    }
  }

  /** Paints one frame into the card's own canvas before revealing it, so the
   *  handoff from the DOM images never flashes an empty canvas. */
  enhance (entry) {
    if (entry.enhanced || !entry.texFrom || this.blitBroken) return

    const canvas = document.createElement('canvas')
    canvas.className = 'prd-Card_Canvas'
    canvas.width = CardShader.renderSize
    canvas.height = CardShader.renderSize
    canvas.setAttribute('aria-hidden', 'true')
    entry.canvas = canvas
    entry.ctx = canvas.getContext('2d')
    // Replace rather than composite: each blit must fully overwrite the previous
    // frame, including its alpha. Under the default source-over, transparent
    // regions of a cut-out PNG never overwrite what's beneath them, so every
    // frame of the dissolve accumulates into a smear.
    entry.ctx.globalCompositeOperation = 'copy'
    entry.media.appendChild(canvas)

    if (!this.paintAndVerify(entry)) {
      this.disableBlit()
      return
    }

    entry.media.classList.add('is-Enhanced')
    entry.enhanced = true
  }

  /**
   * Paints the first frame into the card canvas, and the first time only,
   * confirms the WebGL-to-2D blit actually produced pixels — some engines can
   * silently no-op or throw on drawImage(webglCanvas). Returns false if the
   * blit threw or left the canvas blank, so the whole effect can stand down.
   */
  paintAndVerify (entry) {
    try {
      this.renderEntry(entry)
    } catch (e) {
      this.log('blit threw: ' + e)
      return false
    }
    if (this.blitVerified) return true

    const size = CardShader.renderSize
    const points = [[size / 2, size / 2], [size / 4, size / 4], [size * 3 / 4, size * 3 / 4]]
    for (const [x, y] of points) {
      const px = entry.ctx.getImageData(x, y, 1, 1).data
      if (px[0] || px[1] || px[2] || px[3]) {
        this.blitVerified = true
        return true
      }
    }
    return false
  }

  /**
   * The WebGL-to-2D blit is unusable in this browser — revert every card to
   * the CSS cross-fade and detach, so nothing can ever show a blank canvas.
   */
  disableBlit () {
    this.blitBroken = true
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }
    this.active.clear()
    for (const entry of [...this.entries.values()]) this.teardown(entry)
    document.removeEventListener('mouseover', this.onOver)
    document.removeEventListener('mouseout', this.onOut)
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.log('blit unsupported — reverted all cards to CSS crossfade')
  }

  activate (entry) {
    if (this.blitBroken || !entry.enhanced) return
    this.active.add(entry)
    this.schedule()
  }

  schedule () {
    if (this.frame || document.visibilityState !== 'visible') return
    this.frame = requestAnimationFrame(this.tick)
  }

  tick () {
    this.frame = null
    const gl = this.gl
    if (!gl || gl.isContextLost()) return

    for (const entry of this.active) {
      entry.progress += (entry.target - entry.progress) * CardShader.ease
      const settled = Math.abs(entry.target - entry.progress) < CardShader.epsilon
      if (settled) entry.progress = entry.target

      if (entry.enhanced && entry.texFrom) this.renderEntry(entry)
      if (settled && entry.target === 0) this.active.delete(entry)
    }

    if (this.active.size) this.schedule()
  }

  renderEntry (entry) {
    const gl = this.gl

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, entry.texFrom)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, entry.texTo)
    // Rebound every frame because lazy per-card uploads can leave a different
    // texture on this unit, which would corrupt the displacement map.
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, this.noise)
    gl.uniform1f(this.uFromRatio, entry.fromRatio)
    gl.uniform1f(this.uToRatio, entry.toRatio)
    gl.uniform1f(this.uProgress, entry.progress)
    gl.uniform2f(this.uSeed, entry.seed[0], entry.seed[1])

    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.drawArrays(gl.TRIANGLES, 0, 6)

    entry.ctx.drawImage(this.canvas, 0, 0)
  }

  ensureGL () {
    if (this.gl) return this.ready && !this.gl.isContextLost()

    const canvas = document.createElement('canvas')
    canvas.width = CardShader.renderSize
    canvas.height = CardShader.renderSize

    const gl = canvas.getContext('webgl', {
      premultipliedAlpha: false,
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: true,
      powerPreference: 'low-power',
      failIfMajorPerformanceCaveat: true,
    })
    if (!gl) return false

    this.canvas = canvas
    this.gl = gl

    this.onContextLost = (e) => { e.preventDefault(); this.handleContextLost() }
    this.onContextRestored = () => this.handleContextRestored()
    canvas.addEventListener('webglcontextlost', this.onContextLost, false)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false)

    const highp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)
    this.highpAvailable = !!(highp && highp.precision > 0)
    this.maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE)

    return this.buildProgram()
  }

  buildProgram () {
    const gl = this.gl

    const vs = 'attribute vec2 aPos; attribute vec2 aUV; varying vec2 vUV;' +
      'void main(){ gl_Position = vec4(aPos,0.0,1.0); vUV = aUV; }'

    const fs = 'precision ' + (this.highpAvailable ? 'highp' : 'mediump') + ' float; varying vec2 vUV;' +
      'uniform sampler2D uFrom; uniform sampler2D uTo; uniform sampler2D uDisp;' +
      'uniform float uProgress; uniform float uIntensity; uniform float uBand;' +
      'uniform float uFromRatio; uniform float uToRatio; uniform float uBoxRatio;' +
      'uniform vec2 uSeed;' +
      'float fold(float v){ return 1.0 - abs(1.0 - abs(v)); }' +
      'vec2 cover(vec2 uv, float imgRatio, float boxRatio){' +
      '  vec2 s = vec2(1.0);' +
      '  if (imgRatio > boxRatio) s.x = boxRatio / imgRatio; else s.y = imgRatio / boxRatio;' +
      '  return (uv - 0.5) * s + 0.5;' +
      '}' +
      'void main(){' +
      '  vec2 uv = vUV;' +
      // Per-card offset into the (mirrored-repeat) noise so each card dissolves
      // in a different pattern while the drift direction stays fixed.
      '  float disp = texture2D(uDisp, uv + uSeed).r;' +
      '  float drift = disp * uIntensity;' +
      '  vec2 uvFrom = cover(vec2(uv.x + uProgress * drift, uv.y), uFromRatio, uBoxRatio);' +
      '  vec2 uvTo = cover(vec2(uv.x - (1.0 - uProgress) * drift, uv.y), uToRatio, uBoxRatio);' +
      '  uvFrom = vec2(fold(uvFrom.x), fold(uvFrom.y));' +
      '  uvTo = vec2(fold(uvTo.x), fold(uvTo.y));' +
      '  vec4 f = texture2D(uFrom, uvFrom);' +
      '  vec4 t = texture2D(uTo, uvTo);' +
      '  float p = uProgress * (1.0 + 2.0 * uBand) - uBand;' +
      '  float m = smoothstep(disp - uBand, disp + uBand, p);' +
      // Textures are premultiplied, so mix in premultiplied space (keeps colour
      // true through the fade) then un-premultiply for the straight-alpha context.
      '  vec4 pm = mix(f, t, m);' +
      '  gl_FragColor = vec4(pm.a > 0.0 ? pm.rgb / pm.a : vec3(0.0), pm.a);' +
      '}'

    const prog = this.createProgram(vs, fs)
    if (!prog) return false
    gl.useProgram(prog)
    this.prog = prog

    const quad = new Float32Array([
      -1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0,
      -1, 1, 0, 0, 1, -1, 1, 1, 1, 1, 1, 0,
    ])
    this.buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer)
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW)

    const aPos = gl.getAttribLocation(prog, 'aPos')
    const aUV = gl.getAttribLocation(prog, 'aUV')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0)
    gl.enableVertexAttribArray(aUV)
    gl.vertexAttribPointer(aUV, 2, gl.FLOAT, false, 16, 8)

    this.uProgress = gl.getUniformLocation(prog, 'uProgress')
    this.uFromRatio = gl.getUniformLocation(prog, 'uFromRatio')
    this.uToRatio = gl.getUniformLocation(prog, 'uToRatio')
    this.uSeed = gl.getUniformLocation(prog, 'uSeed')

    gl.uniform1i(gl.getUniformLocation(prog, 'uFrom'), 0)
    gl.uniform1i(gl.getUniformLocation(prog, 'uTo'), 1)
    gl.uniform1i(gl.getUniformLocation(prog, 'uDisp'), 2)
    gl.uniform1f(gl.getUniformLocation(prog, 'uIntensity'), CardShader.intensity)
    gl.uniform1f(gl.getUniformLocation(prog, 'uBand'), CardShader.band)

    // Cards are always square (.prd-Card_Media aspect-ratio 1/1) and rendered
    // into a square buffer, so the box ratio is a constant.
    gl.uniform1f(gl.getUniformLocation(prog, 'uBoxRatio'), 1)
    gl.clearColor(0, 0, 0, 0)

    this.noise = this.makeTexture(this.makeNoise())
    // Mirror-repeat (safe: noise is power-of-two) so each card's random uSeed
    // offset tiles without a hard seam.
    gl.activeTexture(gl.TEXTURE3)
    gl.bindTexture(gl.TEXTURE_2D, this.noise)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, this.noise)

    gl.viewport(0, 0, CardShader.renderSize, CardShader.renderSize)

    this.ready = true
    return true
  }

  createProgram (vsSrc, fsSrc) {
    const gl = this.gl
    const compile = (type, src) => {
      const s = gl.createShader(type)
      gl.shaderSource(s, src)
      gl.compileShader(s)
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        this.log('shader compile error: ' + gl.getShaderInfoLog(s))
        gl.deleteShader(s)
        return null
      }
      return s
    }
    const v = compile(gl.VERTEX_SHADER, vsSrc)
    const f = compile(gl.FRAGMENT_SHADER, fsSrc)
    if (!v || !f) {
      if (v) gl.deleteShader(v)
      if (f) gl.deleteShader(f)
      return null
    }
    const p = gl.createProgram()
    gl.attachShader(p, v)
    gl.attachShader(p, f)
    gl.linkProgram(p)
    gl.deleteShader(v)
    gl.deleteShader(f)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      this.log('program link error: ' + gl.getProgramInfoLog(p))
      gl.deleteProgram(p)
      return null
    }
    return p
  }

  makeTexture (src) {
    const gl = this.gl
    // Upload on a scratch unit so a lazy per-card upload never disturbs the
    // from/to/noise units a live render depends on.
    gl.activeTexture(gl.TEXTURE3)
    const t = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, t)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    // Premultiply on upload so the cross-fade composites cleanly (no grey ghost
    // where the two cut-outs differ) and edge filtering doesn't fringe. The noise
    // is opaque, so this is a no-op for it.
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.fitTexture(src))
    return t
  }

  /**
   * Guards against a CDN or hardware limit handing back something larger than
   * the texture budget, which would fail to upload or blow the GPU cache.
   */
  fitTexture (src) {
    const width = src.naturalWidth || src.width
    const height = src.naturalHeight || src.height
    const largest = Math.max(width, height)
    const limit = Math.min(this.maxTexture, CardShader.textureTarget)
    if (!largest || largest <= limit) return src

    const scale = limit / largest
    const scaled = document.createElement('canvas')
    scaled.width = Math.max(1, Math.round(width * scale))
    scaled.height = Math.max(1, Math.round(height * scale))
    scaled.getContext('2d').drawImage(src, 0, 0, scaled.width, scaled.height)
    return scaled
  }

  makeNoise () {
    const size = CardShader.noiseSize
    const c = document.createElement('canvas')
    c.width = c.height = size
    const x = c.getContext('2d')
    const id = x.createImageData(size, size)
    const d = id.data
    const base = 0.012 * (256 / size)
    const vnoise = (px, py) => {
      let s = 0, amp = 1, freq = base, tot = 0
      for (let o = 0; o < 4; o++) {
        const v = Math.sin(px * freq * 1.7 + o * 2.1) * Math.cos(py * freq * 1.3 + o * 1.3) +
          Math.sin((px + py) * freq * 0.9 + o)
        s += v * amp; tot += amp; amp *= 0.5; freq *= 2.0
      }
      return Math.max(0, Math.min(1, (s / tot) * 0.5 + 0.5))
    }
    for (let y = 0; y < size; y++) {
      for (let px = 0; px < size; px++) {
        const n = vnoise(px, y) * 255
        const i = (y * size + px) * 4
        d[i] = d[i + 1] = d[i + 2] = n
        d[i + 3] = 255
      }
    }
    x.putImageData(id, 0, 0)
    return c
  }

  /**
   * Prefer the smallest srcset entry that still covers the render size, so the
   * texture is not the full-resolution candidate the browser never displayed.
   */
  bestSource (img) {
    const fallback = img.currentSrc || img.src
    const set = img.srcset
    if (!set) return fallback

    const candidates = set.split(',')
      .map(part => {
        const [url, w] = part.trim().split(/\s+/)
        return { url, w: parseInt(w, 10) || 0 }
      })
      .filter(candidate => candidate.url && candidate.w > 0)
      .sort((a, b) => a.w - b.w)

    if (!candidates.length) return fallback

    const fit = candidates.find(candidate => candidate.w >= CardShader.textureTarget)
    return (fit || candidates[candidates.length - 1]).url
  }

  imageRatio (img) {
    return img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 1
  }

  handleContextLost () {
    this.log('context lost')
    this.ready = false
    if (this.frame) { cancelAnimationFrame(this.frame); this.frame = null }

    for (const entry of this.entries.values()) {
      entry.texFrom = null
      entry.texTo = null
      if (entry.enhanced) this.revert(entry)
    }
    this.active.clear()
    this.noise = null
    this.prog = null
    this.buffer = null
  }

  handleContextRestored () {
    this.log('context restored')
    this.buildProgram()
  }

  /** Returns a card to the plain CSS cross-fade fallback. */
  revert (entry) {
    entry.media.classList.remove('is-Enhanced')
    entry.media.classList.remove('is-Loading')
    if (entry.canvas) entry.canvas.remove()
    entry.canvas = null
    entry.ctx = null
    entry.enhanced = false
    entry.progress = 0
    entry.target = 0
  }

  touch (media) {
    const i = this.order.indexOf(media)
    if (i !== -1) this.order.splice(i, 1)
    this.order.push(media)
  }

  evict () {
    while (this.order.length > CardShader.maxEntries) {
      const media = this.order.find(m => {
        const entry = this.entries.get(m)
        return entry && !this.active.has(entry)
      })
      if (!media) return
      this.teardown(this.entries.get(media))
    }
  }

  teardown (entry) {
    const gl = this.gl
    if (gl && !gl.isContextLost()) {
      if (entry.texFrom) gl.deleteTexture(entry.texFrom)
      if (entry.texTo) gl.deleteTexture(entry.texTo)
    }
    this.revert(entry)
    this.active.delete(entry)
    this.entries.delete(entry.media)
    const i = this.order.indexOf(entry.media)
    if (i !== -1) this.order.splice(i, 1)
  }

  whenIdle (fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 500 })
    else setTimeout(fn, 1)
  }

  log (msg) {
    if (this.debug) console.log('[card-shader] ' + msg)
  }

  static get ease () {
    return 0.06
  }

  static get intensity () {
    return 0.3
  }

  static get band () {
    return 0.3
  }

  static get epsilon () {
    return 0.001
  }

  static get renderSize () {
    return 768
  }

  static get textureTarget () {
    return 768
  }

  static get noiseSize () {
    return 128
  }

  static get maxEntries () {
    return 16
  }
}

const shader = new CardShader()
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => shader.init())
} else {
  shader.init()
}
